import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { Beer, GlassWater, Warehouse, BellRing, BookOpen, CheckCircle2, ChevronRight, Gauge, Lock, Martini, MessageSquareWarning, Refrigerator, UserRound } from 'lucide-react';
import { HelpBubble } from '@/help/HelpBubble';
import { AREA_LABEL, finishNight } from '@/data/nights';
import { formatBusinessDate } from '@/domain/businessDate';
import { isManagerOrAbove } from '@/domain/permissions';
import { AREAS, type AreaId } from '@/domain/types';
import { Button, cn, Notice, Spinner } from '@/ui/core';
import { useToast } from '@/ui/overlay';
import { useSession } from '@/app/providers';
import { useBusinessToday } from '@/app/hooks';
import { useWaitingNights } from '@/app/AppLayout';
import { WhoDialog } from '@/app/WhoDialog';
import { LeavePracticeButton } from '@/app/LeavePractice';
import { FeedbackDialog } from '@/features/feedback/FeedbackDialog';
import { brand } from '@/config/brand';
import { areaProgress, useNightData } from '../count/useNight';
import { RecountList } from '../count/RecountList';

const ICON = 'size-8 md:size-9';
const AREA_ICON: Record<AreaId, ReactNode> = {
  back_beer: <Beer className={ICON} aria-hidden />,
  front_beer: <Refrigerator className={ICON} aria-hidden />,
  liquor: <Martini className={ICON} aria-hidden />,
  guns: <Gauge className={ICON} aria-hidden />,
};
const AREA_HELP: Record<AreaId, string> = { back_beer: 'count-back-beer', front_beer: 'count-front-beer', liquor: 'count-liquor', guns: 'liquor-guns' };

/**
 * The front page: tonight's count as one big button per area (like the columns of the
 * sheet), what came in or went out, and anything a manager asked to be counted again.
 */
export function StaffHome() {
  const { actor, mode, settings } = useSession();
  const today = useBusinessToday();
  const toast = useToast();
  const data = useNightData(today);
  const waiting = useWaitingNights();
  const [whoOpen, setWhoOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  if (!data) return <Spinner />;

  const manager = isManagerOrAbove(actor.role);
  const night = data.night;
  const status = night?.status ?? 'counting';
  const nothing = data.items.filter((i) => i.active).length === 0;
  const doneCount = AREAS.filter((a) => night?.areasDone[a]).length;
  const allDone = doneCount === AREAS.length;
  const promoCount = data.moves.filter((m) => m.kind === 'promo_spill').length;
  const broughtCount = data.moves.filter((m) => m.kind === 'added').length;
  const first = actor.name.split(' ')[0]!.replace(/[()]/g, '');

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-4 md:gap-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-3xl font-bold md:text-4xl">Hi {first}</h1>
          <p className="text-muted md:text-lg">Tonight: {formatBusinessDate(today, settings.locale, 'long')}</p>
        </div>
        <span className="flex items-center">
          <button type="button" onClick={() => setWhoOpen(true)} className="flex min-h-12 items-center gap-2 rounded-xl px-3 text-base font-semibold text-accent underline underline-offset-4 hover:bg-surface-2 md:min-h-14 md:text-lg">
            <UserRound className="size-5" aria-hidden /> Not {first}?
          </button>
          <HelpBubble topic="who-is-using" />
        </span>
      </div>

      {mode === 'practice' && (
        <Notice tone="warn" title="Practice mode" action={<LeavePracticeButton />}>
          Red’s drink lists with made-up numbers and people — nothing here is {brand.business}’s real stock. Try anything.
        </Notice>
      )}

      {nothing ? (
        <Notice tone="info" title="Nothing to count yet" action={manager ? <Link to="/admin/data" className="inline-flex min-h-11 items-center rounded-xl bg-accent-fill px-4 font-semibold text-accent-fg">Open an Excel file</Link> : undefined}>
          {manager ? 'Bring the lists back from the latest week’s Excel file, or add drinks in the manager area (Drink lists).' : 'A manager needs to set up the drink lists first.'}
        </Notice>
      ) : status === 'counting' ? (
        <>
          <RecountList />
          <Section title="Tonight’s count" aside={`${doneCount} of ${AREAS.length} done`}>
            {AREAS.map((a) => {
              const p = areaProgress(data, today, a);
              const done = !!night?.areasDone[a];
              const to = a === 'guns' ? `/guns/${today}` : `/count/${today}/${a}`;
              const sub = done ? 'Done ✓' : a === 'guns' ? `${p.counted} of 2 readings` : `${p.counted} of ${p.total} counted`;
              const share = a === 'guns' ? p.counted / 2 : p.total ? p.counted / p.total : 0;
              return <BigAction key={a} to={to} icon={done ? <CheckCircle2 className={ICON} aria-hidden /> : AREA_ICON[a]} title={AREA_LABEL[a]} subtitle={sub} done={done} progress={done ? 1 : share} help={AREA_HELP[a]} />;
            })}
          </Section>
          <Section title="During the shift">
            <BigAction to={`/promo/${today}`} icon={<GlassWater className={ICON} aria-hidden />} title="Promo or spillage" subtitle={promoCount ? `${promoCount} so far tonight` : 'Given away, spilled or broken'} help="promo-spill" />
            <BigAction to={`/brought/${today}`} icon={<Warehouse className={ICON} aria-hidden />} title="Brought from downstairs" subtitle={broughtCount ? `${broughtCount} so far tonight` : 'Bottles and cases brought up'} help="brought-up" />
          </Section>
          {allDone ? (
            <div className="relative">
              <Button
                size="xl"
                variant="ok"
                block
                busy={finishing}
                className="min-h-24 text-2xl md:text-3xl"
                icon={<CheckCircle2 className="size-8" aria-hidden />}
                onClick={async () => {
                  setFinishing(true);
                  try {
                    await finishNight(today);
                    toast.show('Tonight’s count is finished. Thank you!');
                  } catch (err) {
                    toast.error(err);
                  } finally {
                    setFinishing(false);
                  }
                }}
              >
                Finish tonight’s count
              </Button>
              <HelpBubble topic="finish-count" className="absolute right-2 top-2 text-white" tone="onAccent" />
            </div>
          ) : (
            <p className="text-center text-base text-muted">When all four are done, a green “Finish tonight’s count” button shows up here.</p>
          )}
        </>
      ) : (
        <>
          <RecountList />
          <div className="flex items-center gap-4 rounded-3xl border-2 border-ok/60 bg-ok-soft p-5 md:p-6">
            <CheckCircle2 className="size-12 shrink-0 text-ok" aria-hidden />
            <div>
              <p className="text-2xl font-bold md:text-3xl">{status === 'approved' ? 'Tonight’s count is approved' : 'Tonight’s count is finished'}</p>
              <p className="text-base md:text-lg">{status === 'approved' ? 'A manager checked it. Thank you!' : 'A manager will check it. Thank you!'}</p>
            </div>
          </div>
          <Section title="During the shift">
            <BigAction to={`/promo/${today}`} icon={<GlassWater className={ICON} aria-hidden />} title="Promo or spillage" subtitle="See tonight’s list" help="promo-spill" />
            <BigAction to={`/brought/${today}`} icon={<Warehouse className={ICON} aria-hidden />} title="Brought from downstairs" subtitle="See tonight’s list" help="brought-up" />
          </Section>
        </>
      )}

      {manager && waiting > 0 && (
        <Link to="/admin/nights" className="flex min-h-16 items-center gap-3 rounded-2xl border-2 border-warn bg-warn-soft px-4 text-lg font-semibold">
          <BellRing className="size-6 text-warn" aria-hidden />
          <span className="flex-1">
            {waiting} night{waiting === 1 ? ' is' : 's are'} waiting for a manager
            <span className="block text-sm font-normal text-muted">Check the counts and approve them, or ask for a recount</span>
          </span>
          <ChevronRight className="size-6 text-muted" aria-hidden />
        </Link>
      )}

      {manager && (
        <Link to="/admin" className="mt-2 flex min-h-16 items-center gap-3 rounded-2xl border-2 border-dashed border-border px-4 text-lg font-semibold hover:bg-surface-2">
          <Lock className="size-6 text-muted" aria-hidden />
          <span className="flex-1">
            Manager area
            <span className="block text-sm font-normal text-muted">Approving nights, the weekly Excel file, drink lists, trends, specials</span>
          </span>
          <ChevronRight className="size-6 text-muted" aria-hidden />
        </Link>
      )}

      <Link to="/help" className="flex min-h-16 items-center gap-3 rounded-2xl border-2 border-border bg-surface px-4 text-lg font-semibold hover:border-accent">
        <BookOpen className="size-6 text-accent" aria-hidden />
        <span className="flex-1">
          How to use the app
          <span className="block text-sm font-normal text-muted">Step-by-step help for everything on this tablet</span>
        </span>
        <ChevronRight className="size-6 text-muted" aria-hidden />
      </Link>
      <button type="button" onClick={() => setFeedbackOpen(true)} className="flex min-h-16 items-center gap-3 rounded-2xl border-2 border-border bg-surface px-4 text-left text-lg font-semibold hover:border-accent">
        <MessageSquareWarning className="size-6 text-accent" aria-hidden />
        <span className="flex-1">
          Report a problem or an idea
          <span className="block text-sm font-normal text-muted">Something wrong, confusing, or could be better? Tell us here.</span>
        </span>
        <ChevronRight className="size-6 text-muted" aria-hidden />
      </button>
      <WhoDialog open={whoOpen} onClose={() => setWhoOpen(false)} />
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </div>
  );
}

/** A heading over a grid of big buttons that are all the same size. */
function Section({ title, aside, children }: { title: string; aside?: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="flex items-baseline justify-between gap-2 px-1 text-xl font-bold md:text-2xl">
        {title}
        {aside && <span className="text-base font-semibold text-muted md:text-lg">{aside}</span>}
      </h2>
      <div className="grid auto-rows-fr gap-3 sm:grid-cols-2 md:gap-4">{children}</div>
    </section>
  );
}

/**
 * A big button. On a phone: icon beside the name. On a tablet: icon above the name, so
 * long names fit on one line and every button in a grid is the same size.
 */
function BigAction({ to, icon, title, subtitle, done, help, progress }: { to: string; icon: ReactNode; title: string; subtitle: ReactNode; done?: boolean; help?: string; progress?: number }) {
  return (
    <div className="relative h-full">
      <Link
        to={to}
        className={cn(
          'flex h-full min-h-24 w-full items-center gap-4 rounded-3xl border-2 p-4 pr-14 text-left transition active:scale-[0.99] sm:flex-col sm:items-start sm:gap-3 sm:p-5',
          done ? 'border-ok/60 bg-ok-soft' : 'border-border bg-surface hover:border-accent',
        )}
      >
        <span className={cn('flex size-14 shrink-0 items-center justify-center rounded-2xl md:size-16', done ? 'bg-ok/15 text-ok' : 'bg-surface-2 text-accent')}>{icon}</span>
        <span className="flex min-w-0 flex-1 flex-col self-stretch justify-center sm:w-full sm:justify-end">
          <span className="block text-2xl font-bold leading-tight lg:text-[1.75rem]">{title}</span>
          <span className={cn('mt-1 block text-base md:text-lg', done ? 'font-semibold text-ok' : 'text-muted')}>{subtitle}</span>
          {progress != null && (
            <span role="progressbar" aria-label={`${title} progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)} className="mt-2 block h-2.5 w-full overflow-hidden rounded-full bg-surface-2">
              <span className={cn('block h-full rounded-full', done ? 'bg-ok' : 'bg-accent-fill')} style={{ width: `${Math.round(progress * 100)}%` }} />
            </span>
          )}
        </span>
      </Link>
      {help && <HelpBubble topic={help} className="absolute right-2 top-2" />}
    </div>
  );
}
